export type ProgressStep = { label: string; done: boolean };

/**
 * A "goal-gradient" setup tracker: a runner along a gradient track with a flag at
 * the goal, plus a checklist of the steps. The runner sits at the share of steps
 * done, and the near-goal brightening is the whole point — the closer to the flag,
 * the more the track glows. When every step is done the runner reaches the flag and
 * the copy celebrates.
 */
export function ProfileProgress({ steps }: { steps: ProgressStep[] }) {
  const total = steps.length;
  const doneCount = steps.filter((s) => s.done).length;
  const allDone = total > 0 && doneCount === total;
  const pct = total ? (doneCount / total) * 100 : 0;
  const next = steps.find((s) => !s.done);

  return (
    <section
      aria-label="Setup progress"
      className="rounded-2xl border-l-4 border-brand-blue px-4 py-4"
      style={{
        background: "linear-gradient(120deg, hsl(var(--muted) / 0.5), hsl(var(--accent) / 0.4))",
      }}
    >
      <h2 className="font-comic-headline text-base leading-tight">
        {allDone ? "You're all set 🎉" : "Almost there"}
      </h2>
      {/* The last branch is a space, not "": an empty string makes React drop the
          text node entirely, and dropping a node a translator has re-parented is
          what throws NotFoundError. It also keeps the line from collapsing. */}
      <p className="mt-0.5 text-xs text-muted-foreground">
        {allDone ? "Your agent's ready to go." : next ? `Next: ${next.label.toLowerCase()}.` : " "}
      </p>

      {/* The track */}
      <div className="relative mt-6 mb-4 h-3">
        <div className="absolute inset-x-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-foreground/[0.07]" />
        <div
          className="absolute left-0 top-1/2 h-2 -translate-y-1/2 rounded-full transition-[width] duration-700"
          style={{
            width: `${Math.max(pct, 6)}%`,
            background: "linear-gradient(90deg, #a5b4fc, #67e8f9, #86efac)",
          }}
        />
        <Flag at={100} reached={allDone} />
        <Runner at={pct} celebrating={allDone} />
      </div>

      {/* Steps checklist */}
      <ul className="space-y-1.5">
        {steps.map((s) => (
          <li key={s.label} className="flex items-center gap-2 text-[11px]">
            <span
              className={`grid h-4 w-4 shrink-0 place-items-center rounded-full text-[9px] font-bold ${
                s.done
                  ? "bg-brand-blue text-brand-blue-foreground"
                  : "border border-foreground/25 text-transparent"
              }`}
            >
              ✓
            </span>
            <span className={s.done ? "text-foreground" : "text-muted-foreground"}>{s.label}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Flag({ at, reached }: { at: number; reached: boolean }) {
  return (
    <div className="absolute top-1/2 -translate-y-1/2" style={{ left: `${at}%` }}>
      <svg width="20" height="24" viewBox="0 0 20 24" fill="none" className="-ml-3 -translate-y-3" aria-hidden>
        <line x1="4" y1="2" x2="4" y2="23" stroke="hsl(var(--muted-foreground))" strokeWidth="2" strokeLinecap="round" />
        <path
          d="M4 3 H16 L13 7 L16 11 H4 Z"
          fill={reached ? "#86efac" : "#fda4af"}
          stroke="rgba(0,0,0,.18)"
          strokeWidth="1"
        />
      </svg>
    </div>
  );
}

/** A stylised runner, mid-stride. Static (a gentle idle bob), positioned by `at`. */
function Runner({ at, celebrating }: { at: number; celebrating: boolean }) {
  return (
    <div
      className="scheda-runner absolute top-1/2"
      style={{ left: `${at}%`, transform: "translate(-50%, -85%)" }}
      aria-hidden
    >
      <svg width="34" height="34" viewBox="0 0 40 40" fill="none">
        <g
          stroke={celebrating ? "#059669" : "#4f46e5"}
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="27" cy="8" r="4.2" fill={celebrating ? "#a7f3d0" : "#c7d2fe"} strokeWidth="2" />
          <path d="M25 12 L18 24" />
          <path d="M18 24 L25 27 L27 36" />
          <path d="M18 24 L11 27 L7 22" />
          <path d="M22 16 L30 18 L33 13" />
          <path d="M22 16 L14 19 L12 25" />
        </g>
      </svg>
    </div>
  );
}
